import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Camera, ScanLine, Plus, Loader2, Search, AlertCircle, CheckCircle2, X,
  Sparkles, Upload, Wand2, Wine, Package,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { units } from "../../lib/utils";

/**
 * Quick-add ingredient dialog with 3 modes:
 *  - Foto IA: shelf photo → Gemini Vision → batch-create ingredients
 *  - Código de barras: scan/type EAN → Open Food Facts → create single ingredient
 *  - Manual: classic form
 *
 * Centralizes all ingredient-creation flows that used to live in different
 * places (Inventory manual form + Recipes' NewBeverageDialog).
 */

async function resizeImage(file, maxSide = 1280, quality = 0.82) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve({ blob, dataUrl: reader.result });
      reader.readAsDataURL(blob);
    }, "image/jpeg", quality);
  });
}

export function QuickAddIngredientDialog({ open, onOpenChange, restaurantId, onCreated }) {
  const [mode, setMode] = useState("photo");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quick-add-ingredient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" /> Alta rápida de producto
          </DialogTitle>
          <DialogDescription>
            Elige el método más rápido para dar de alta nuevos productos en tu inventario.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="photo" className="gap-1.5" data-testid="qa-tab-photo">
              <Camera className="w-4 h-4" /> Foto IA
            </TabsTrigger>
            <TabsTrigger value="barcode" className="gap-1.5" data-testid="qa-tab-barcode">
              <ScanLine className="w-4 h-4" /> Código de barras
            </TabsTrigger>
            <TabsTrigger value="manual" className="gap-1.5" data-testid="qa-tab-manual">
              <Package className="w-4 h-4" /> Manual
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photo" className="pt-4">
            <PhotoTab restaurantId={restaurantId} onCreated={onCreated} onClose={() => onOpenChange(false)} />
          </TabsContent>
          <TabsContent value="barcode" className="pt-4">
            <BarcodeTab restaurantId={restaurantId} onCreated={onCreated} onClose={() => onOpenChange(false)} active={mode === "barcode"} />
          </TabsContent>
          <TabsContent value="manual" className="pt-4">
            <ManualTab restaurantId={restaurantId} onCreated={onCreated} onClose={() => onOpenChange(false)} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Foto IA Tab ──────────────────────────────────────────────────────
function PhotoTab({ restaurantId, onCreated, onClose }) {
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [items, setItems] = useState([]); // [{detected_name, ingredient_id, matched_name, selected, unit, est_cost}]
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setB64(null); setItems([]); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato no soportado (JPG, PNG, WEBP)");
      return;
    }
    reset();
    const { dataUrl } = await resizeImage(file);
    setPreview(dataUrl);
    setB64(String(dataUrl).split(",")[1]);
  };

  const handleAnalyze = async () => {
    if (!b64) return;
    setAnalyzing(true);
    try {
      const r = await api.analyzeShelfPhoto(restaurantId, b64);
      const enriched = (r.items || []).map((it) => ({
        ...it,
        selected: !it.ingredient_id, // only new ones by default
        unit: it.unit || "l",
        est_cost: 10,
      }));
      setItems(enriched);
      const newCount = enriched.filter((i) => !i.ingredient_id).length;
      toast.success(`${enriched.length} detectado(s)`, {
        description: `${newCount} son productos nuevos para dar de alta`,
      });
    } catch (e) {
      toast.error(e.message || "Error al analizar");
    } finally {
      setAnalyzing(false);
    }
  };

  const toggle = (i) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, selected: !it.selected } : it));
  const setField = (i, field, val) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

  const handleCreateAll = async () => {
    const toCreate = items.filter((i) => i.selected && !i.ingredient_id);
    if (toCreate.length === 0) { toast.info("Nada que crear"); return; }
    setCreating(true);
    let created = 0;
    try {
      for (const it of toCreate) {
        try {
          const ing = await api.createIngredient(restaurantId, {
            name: it.detected_name,
            unit: it.unit || "l",
            cost_per_unit: parseFloat(it.est_cost) || 0,
            waste_percentage: 0,
            min_stock: 0,
            current_stock: 0,
          });
          // Fire-and-forget: AI image generation in background so the dialog
          // closes fast and the grid refreshes when done.
          api.generateIngredientImageById?.(ing.ingredient_id).catch(() => {});
          created += 1;
        } catch (e) {
          console.error("create failed", it.detected_name, e);
        }
      }
      toast.success(`${created} ingrediente(s) creado(s)`);
      onCreated?.();
      onClose?.();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Fotografía la estantería o barra y la IA detectará cada botella. Selecciona
        cuáles quieres crear en tu inventario.
      </p>

      {!preview ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
          <Camera className="w-10 h-10 text-muted-foreground mx-auto" />
          <input
            ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            capture="environment" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="qa-photo-file-input"
          />
          <Button onClick={() => fileRef.current?.click()} data-testid="qa-photo-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Elegir foto
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden border">
            <img src={preview} alt="" className="w-full max-h-52 object-contain bg-muted" />
            <Button variant="outline" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-background/90" onClick={reset}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          {items.length === 0 && !analyzing && (
            <Button onClick={handleAnalyze} className="w-full" data-testid="qa-photo-analyze-btn">
              <Wand2 className="w-4 h-4 mr-1.5" /> Analizar con IA
            </Button>
          )}
          {analyzing && (
            <div className="border rounded-lg p-4 flex items-center gap-3 text-sm bg-muted/40">
              <Loader2 className="w-5 h-5 animate-spin text-primary" /> Analizando…
            </div>
          )}

          {items.length > 0 && (
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Detectado ({items.length})</span>
                <span className="text-xs text-muted-foreground">
                  {items.filter((i) => i.selected).length} seleccionado(s)
                </span>
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {items.map((it, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${it.ingredient_id ? "bg-muted/40 opacity-70" : "bg-background"}`}
                    data-testid={`qa-photo-row-${i}`}
                  >
                    <input
                      type="checkbox" checked={!!it.selected}
                      disabled={!!it.ingredient_id}
                      onChange={() => toggle(i)}
                      className="h-4 w-4"
                    />
                    <span className="flex-1 truncate">
                      <span className="font-medium">{it.detected_name}</span>
                      {it.ingredient_id && (
                        <Badge variant="secondary" className="ml-1.5 text-[9px]">ya existe</Badge>
                      )}
                    </span>
                    {!it.ingredient_id && (
                      <>
                        <Select value={it.unit} onValueChange={(v) => setField(i, "unit", v)}>
                          <SelectTrigger className="h-7 w-20 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {units.map((u) => <SelectItem key={u.value} value={u.value}>{u.value}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="relative w-20">
                          <Input
                            type="number" step="0.01" min="0" value={it.est_cost}
                            onChange={(e) => setField(i, "est_cost", e.target.value)}
                            className="h-7 text-xs pl-4"
                            data-testid={`qa-photo-cost-${i}`}
                          />
                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">€</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <Button
                onClick={handleCreateAll} disabled={creating || items.filter((i) => i.selected && !i.ingredient_id).length === 0}
                className="w-full" data-testid="qa-photo-create-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                Crear {items.filter((i) => i.selected && !i.ingredient_id).length} producto(s)
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Barcode Tab ──────────────────────────────────────────────────────
function BarcodeTab({ restaurantId, onCreated, onClose, active }) {
  const [mode, setMode] = useState("scan");
  const [manualCode, setManualCode] = useState("");
  const [looking, setLooking] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({ name: "", unit: "l", cost: "10", barcode: "" });
  const [creating, setCreating] = useState(false);
  const scannerInstanceRef = useRef(null);

  useEffect(() => {
    if (!active) stopScanner();
    return () => stopScanner();
  }, [active]);

  async function startScanner() {
    if (scannerInstanceRef.current) return;
    if (typeof window === "undefined" || !navigator?.mediaDevices?.getUserMedia) {
      toast.error("Tu navegador no permite cámara. Usa la pestaña de código manual.");
      return;
    }
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      toast.error("La cámara requiere HTTPS");
      return;
    }
    try {
      const mod = await import("html5-qrcode");
      const elId = "qa-barcode-scanner";
      if (!document.getElementById(elId)) return;
      const instance = new mod.Html5Qrcode(elId);
      scannerInstanceRef.current = instance;
      setScanning(true);
      await instance.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 160 } },
        async (code) => {
          try { await instance.stop(); } catch { /* noop */ }
          scannerInstanceRef.current = null;
          setScanning(false);
          await handleLookup(code);
        },
        () => {}
      );
    } catch (err) {
      scannerInstanceRef.current = null;
      setScanning(false);
      toast.error("No se pudo iniciar la cámara");
    }
  }

  function stopScanner() {
    const inst = scannerInstanceRef.current;
    scannerInstanceRef.current = null;
    setScanning(false);
    if (inst) {
      try {
        const p = inst.stop();
        if (p && typeof p.catch === "function") {
          p.catch(() => {}).finally(() => { try { inst.clear(); } catch { /* noop */ } });
        }
      } catch { /* noop */ }
    }
  }

  const handleLookup = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    setLooking(true);
    try {
      const r = await api.lookupBarcode(code);
      setLookup(r);
      setForm((f) => ({
        ...f,
        barcode: code,
        name: r.found ? (r.name || `Producto ${code}`) : "",
        unit: r.found && r.is_beverage ? "l" : f.unit,
      }));
      if (r.found) toast.success(`Encontrado: ${r.name || code}`);
      else toast.warning("No encontrado en Open Food Facts. Rellena los datos.");
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setLooking(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Nombre obligatorio"); return; }
    setCreating(true);
    try {
      const ing = await api.createIngredient(restaurantId, {
        name: form.name.trim(),
        unit: form.unit,
        cost_per_unit: parseFloat(form.cost) || 0,
        waste_percentage: 0,
        min_stock: 0,
        current_stock: 0,
        image_url: lookup?.image_url || undefined,
      });
      toast.success(`"${ing.name}" añadido al inventario`);
      onCreated?.(ing);
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Error al crear");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      <Tabs value={mode} onValueChange={(v) => { setMode(v); if (v !== "scan") stopScanner(); }}>
        <TabsList className="w-full">
          <TabsTrigger value="scan" className="flex-1 gap-1.5"><Camera className="w-4 h-4" /> Escanear</TabsTrigger>
          <TabsTrigger value="type" className="flex-1 gap-1.5"><ScanLine className="w-4 h-4" /> Teclear</TabsTrigger>
        </TabsList>

        <TabsContent value="scan" className="space-y-3 pt-3">
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-muted relative max-h-64">
            <div id="qa-barcode-scanner" className="w-full h-full" data-testid="qa-barcode-scanner" />
            {!scanning && (
              <div className="absolute inset-0 flex items-center justify-center text-center text-muted-foreground p-4 pointer-events-none">
                <div>
                  <Camera className="w-10 h-10 mx-auto mb-2" />
                  <p className="text-sm">Pulsa &ldquo;Iniciar cámara&rdquo;</p>
                </div>
              </div>
            )}
          </div>
          {!scanning ? (
            <Button onClick={startScanner} className="w-full gap-1.5" data-testid="qa-barcode-start-btn">
              <Camera className="w-4 h-4" /> Iniciar cámara
            </Button>
          ) : (
            <Button onClick={stopScanner} variant="outline" className="w-full gap-1.5">
              <X className="w-4 h-4" /> Detener
            </Button>
          )}
        </TabsContent>

        <TabsContent value="type" className="space-y-2 pt-3">
          <Label>Código de barras</Label>
          <div className="flex gap-2">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g, ""))}
              placeholder="Ej. 5449000000996" maxLength={20}
              data-testid="qa-barcode-manual-input"
            />
            <Button onClick={() => handleLookup(manualCode)} disabled={looking || manualCode.length < 6} data-testid="qa-barcode-lookup-btn">
              {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {lookup && !lookup.found && (
        <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 flex items-start gap-2 text-sm" data-testid="qa-barcode-notfound">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
          <div>
            <p className="font-medium">No encontrado</p>
            <p className="text-xs text-muted-foreground">Puedes completar los datos manualmente. Código: {lookup.barcode}</p>
          </div>
        </div>
      )}
      {lookup && lookup.found && (
        <div className="border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg p-3 flex items-center gap-3 text-sm" data-testid="qa-barcode-found">
          {lookup.image_url && <img src={lookup.image_url} alt="" className="w-16 h-16 object-contain bg-card rounded border" />}
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{lookup.name}</p>
            {lookup.brand && <p className="text-xs text-muted-foreground">Marca: {lookup.brand}</p>}
          </div>
        </div>
      )}

      {lookup && (
        <div className="border-t pt-3 space-y-2">
          <div>
            <Label>Nombre *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="qa-barcode-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Unidad</Label>
              <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                <SelectTrigger data-testid="qa-barcode-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Coste / unidad (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} data-testid="qa-barcode-cost-input" />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating || !form.name.trim()} className="w-full" data-testid="qa-barcode-create-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Crear en inventario
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Manual Tab ──────────────────────────────────────────────────────
function ManualTab({ restaurantId, onCreated, onClose }) {
  const [form, setForm] = useState({ name: "", unit: "l", cost: "", stock: "0", min_stock: "0" });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Nombre obligatorio");
    setCreating(true);
    try {
      const ing = await api.createIngredient(restaurantId, {
        name: form.name.trim(),
        unit: form.unit,
        cost_per_unit: parseFloat(form.cost) || 0,
        waste_percentage: 0,
        min_stock: parseFloat(form.min_stock) || 0,
        current_stock: parseFloat(form.stock) || 0,
      });
      toast.success(`"${ing.name}" añadido`);
      onCreated?.(ing);
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Nombre *</Label>
        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej. Harina de trigo" data-testid="qa-manual-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Unidad</Label>
          <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
            <SelectTrigger data-testid="qa-manual-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{units.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Coste / unidad (€)</Label>
          <Input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} data-testid="qa-manual-cost" />
        </div>
        <div>
          <Label>Stock inicial</Label>
          <Input type="number" step="0.01" min="0" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} data-testid="qa-manual-stock" />
        </div>
        <div>
          <Label>Stock mínimo</Label>
          <Input type="number" step="0.01" min="0" value={form.min_stock} onChange={(e) => setForm((f) => ({ ...f, min_stock: e.target.value }))} data-testid="qa-manual-min" />
        </div>
      </div>
      <DialogFooter className="pt-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleCreate} disabled={creating || !form.name.trim()} data-testid="qa-manual-create-btn">
          {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Crear ingrediente
        </Button>
      </DialogFooter>
    </div>
  );
}

export default QuickAddIngredientDialog;
